<script lang="ts">
	import { dayjs } from '@woltair/translations-package-fe'
	import { onMount } from 'svelte'
	import IntersectionObserver from 'svelte-intersection-observer'
	import { sendGTM } from '$lib/analytics'
	import { ORGANIZATION } from '$lib/consts'
	import DynamicZone from '$lib/modules/cms/DynamicZone/DynamicZone.svelte'
	import { DOCUMENTS, WOLTADVISOR, LEAVE_CONTACT, BLOG, MEETING } from '$lib/routes'
	import { t, tTag } from '$lib/translations'
	import { Anchor } from '$lib/ui-woltair/components'
	import { resizeCDNImage } from '$lib/utils'
	import { PUBLIC_WOLTAIR_WEB_CODE } from '$env/static/public'

	type Article = {
		__typename?: string
		title?: string
		perex?: string
		slug?: string
		publishDate?: string
		createdAt?: string
		metaTitle?: string
		metaDescription?: string
		legacy?: true
		type?: string
		originalCategory?: string
		hideBottomCTA?: boolean
		author?: {
			data: {
				attributes: {
					name: string
					description: string
					photo: {
						data: {
							attributes: {
								url: string
							}
						}
					}
				}
			}
		}
		button?: {
			text: string
			link: string
			hideButton: boolean
		}
		content?: {
			__typename: string
			id: string
			text: string
		}[]
		categories?: {
			data: {
				attributes: {
					title: string
					slug: string
				}
			}[]
		}
		mainImage?: {
			data: {
				attributes: {
					url: string
				}
			}[]
		}
	}

	export let origin = ''
	export let path = ''
	export let article: Article = {}
	export let showBreadcrumbs = false
	export let CTA = false
	export let components: any

	const dateFormat: string =
		{
			WOLTAIR_CZ: 'D. MMMM YYYY',
			WOLTAIR_PL: 'D.MM.YYYY',
			WOLTAIR_DE: 'D. MMMM YYYY',
			WOLTAIR_IT: 'D MMMM YYYY'
		} || 'D. MMMM YYYY'

	const imgUrlResized = resizeCDNImage(article?.mainImage?.data[0].attributes.url, 1328)

	const author = article?.author?.data?.attributes

	const buttonText = article?.button?.text || t('web.blog.headerButton.defaultText')
	const buttonLink = article?.button?.link || `/${WOLTADVISOR}`

	let processedComponents: any[] = []

	let textBeforeButton: string = ''

	if (
		components[0].__typename === 'ComponentArticlesTextBlock' &&
		article?.button?.hideButton !== true
	) {
		const text = components[0].text
		const pEnd = text.indexOf('</p>')

		const button = `<div class="w-full flex justify-center my-8"><a href="${buttonLink}" class="button-after-perex btn btn-new btn-new-red !text-white whitespace-normal max-w-full w-full md:w-fit">${buttonText}</a></div>`

		const modifiedText = text.slice(0, pEnd + 4) + button + text.slice(pEnd + 4)

		processedComponents = [
			{
				...components[0],
				text: modifiedText
			},
			...components.slice(1)
		]

		textBeforeButton = text.slice(0, pEnd + 4) + button
	}

	let header: HTMLElement
	let headerVisible: boolean = true
	let footer: HTMLElement
	let footerVisible: boolean = false
	let articleWrapper: HTMLElement
	let articleWrapperVisible: boolean = true

	onMount(() => {
		const buttons = document.querySelectorAll('.button-after-perex')

		buttons.forEach((button) => {
			if (button)
				button.addEventListener('click', () => {
					sendGTM(
						'blog_call_to_action',
						{},
						{
							content: {
								type: 'click_button_blog',
								blog_name: article.slug,
								name: buttonText
							}
						}
					)
				})
		})
	})
</script>

{#if article}
	<div
		class="fixed inset-x-0 bottom-2 z-[999] mx-2 translate-y-[150%] transition-transform md:hidden"
		class:!translate-y-0={headerVisible === false &&
			footerVisible === false &&
			articleWrapperVisible === true}
	>
		<a
			href={buttonLink}
			class="btn btn-new btn-new-blue w-full max-w-full whitespace-normal"
			on:click={() => {
				sendGTM(
					'blog_call_to_action',
					{},
					{
						content: {
							type: 'click_button_blog',
							blog_name: article.slug,
							name: buttonText
						}
					}
				)
			}}>{buttonText}</a
		>
	</div>

	<article class="blog-section" itemscope itemtype="https://schema.org/Article">
		<meta itemprop="mainEntityOfPage" content="{origin}{path}" />
		<meta
			content={dayjs(article.publishDate || article.createdAt).format('YYYY-MM-DD')}
			itemprop="datePublished"
		/>

		{#if article.categories?.data?.length && !article.categories?.data[0].attributes?.slug?.includes(DOCUMENTS) && showBreadcrumbs}
			<div class="flex w-full items-end justify-between pt-2 text-xs">
				<div class="flex flex-wrap items-center gap-3 sm:flex-nowrap">
					<a class="hover:text-w text-w-red-500 hover:underline" href="/"
						>{ORGANIZATION[PUBLIC_WOLTAIR_WEB_CODE].domain}</a
					>
					<span class="h-fit"
						><img
							src="/images/icons/arrow/chevron-black.svg"
							alt="Arrow icon"
							class="h-[.675em]"
						/></span
					>
					<a class="text-w-blue-950 hover:underline" href="/{BLOG}">{t('web.blog.title')}</a>
					{#if article.categories?.data[0].attributes.title}
						<span class="h-fit"
							><img
								src="/images/icons/arrow/chevron-black.svg"
								alt="Arrow icon"
								class="h-[.675em]"
							/></span
						>
						<a
							class="text-w-blue-950 hover:underline"
							href="/{BLOG}/{article.categories?.data[0].attributes.slug}"
							>{article.categories?.data[0].attributes.title}</a
						>
					{/if}
				</div>
				<div class="link-underline hidden md:block">
					{@html t('web.dynamic_pages.header.contactExpert', {
						class: 'text-w-red-500',
						link: `/${LEAVE_CONTACT}`
					})}
				</div>
			</div>
		{/if}

		<div class="heading-cont mb-8 mt-8 sm:mt-16">
			<Anchor padded id="blog" />
			<div class="sm:mx-auto md:max-w-[80%]">
				<h1 class="h1 text-left text-secondary-500 sm:text-center">{@html article.title}</h1>
				<meta itemprop="headline" content={article.title} />
				<div itemprop="publisher" itemscope itemtype="https://schema.org/Organization">
					<div itemprop="logo" itemscope itemtype="https://schema.org/ImageObject">
						<meta itemprop="url" content="{origin}/images/logos/woltair.svg" />
					</div>
					<meta itemprop="name" content="Woltair" />
				</div>
			</div>
			{#if author}
				<div
					class="flex flex-wrap items-center justify-start gap-4 sm:mt-8 sm:justify-center"
					itemprop="author"
					itemscope
					itemtype="https://schema.org/Person"
				>
					{#if author?.photo?.data?.attributes?.url}
						<img
							class="h-8 w-8 rounded-full"
							itemprop="image"
							src={author?.photo?.data?.attributes?.url}
							alt={author.name}
						/>
					{/if}
					<p class="unstyled !my-0 text-xs font-semibold sm:text-sm" itemprop="name">
						{author?.name}
					</p>
					<p class="unstyled !my-0 ml-2 text-xs text-neutral-400 sm:text-sm">
						{dayjs(article.publishDate || article.createdAt).format(
							dateFormat[PUBLIC_WOLTAIR_WEB_CODE]
						)}
					</p>
				</div>
			{/if}
		</div>
		{#if article.perex}
			<div itemprop="abstract" class="relative mx-auto mb-16 block text-center md:w-[60%]">
				<p class="unstyled text-neutral-600">{article.perex}</p>
			</div>
		{/if}

		<div class="my-2 text-center sm:my-8">
			<div class="relative flex aspect-[5/4] overflow-hidden rounded-3xl sm:aspect-[2/1]">
				{#if imgUrlResized}
					<img
						itemprop="image"
						class="min-h-full min-w-full max-w-none object-cover object-center"
						src={imgUrlResized}
						alt={article.title}
						width="1328"
						height="664"
					/>
				{:else}
					<img
						itemprop="image"
						class="min-h-full min-w-full max-w-none object-cover object-center"
						src="/images/illustrations/woltair-house.svg"
						alt={tTag('web.components.header.illustration.imgAlt')}
					/>
				{/if}
			</div>
		</div>

		<IntersectionObserver element={articleWrapper} bind:intersecting={articleWrapperVisible}>
			<div class="relative flex justify-center" bind:this={articleWrapper}>
				<IntersectionObserver element={header} bind:intersecting={headerVisible} threshold={0}>
					<div
						bind:this={header}
						class="blog-cont pointer-events-none !absolute h-fit w-full opacity-0"
					>
						{@html textBeforeButton}
					</div>
				</IntersectionObserver>
				<div class="flex w-full flex-col gap-4 md:w-[60%]">
					<DynamicZone
						componentData={(processedComponents.length && processedComponents) || components}
					/>
				</div>
			</div>
		</IntersectionObserver>
		{#if CTA && !article.hideBottomCTA}
			<div class="blog-cont max-w-ful h-fit overflow-y-visible">
				<section
					class="column-xl mx-auto my-10 flex max-w-[800px] flex-col items-center gap-8 rounded-2xl bg-neutral-100 p-8 md:flex-row"
				>
					<h3 class="!m-0">
						{t('web.blog.cta.title')}
					</h3>
					<div class="flex flex-col items-stretch gap-2">
						<a
							href="/{WOLTADVISOR}"
							class="btn-new btn-new-red unstyled w-full text-sm !text-white"
						>
							{t('web.blog.cta.calc')}
						</a>

						{#if PUBLIC_WOLTAIR_WEB_CODE === 'WOLTAIR_CZ'}
							<a href="/{MEETING}" class="btn-new btn-new-blue w-full self-stretch text-sm">
								{t('web.blog.cta.expert')}
							</a>
						{/if}
					</div>
				</section>
			</div>
		{/if}
	</article>
	<IntersectionObserver element={footer} bind:intersecting={footerVisible}>
		<div class="h-0 w-full" bind:this={footer} />
	</IntersectionObserver>
{/if}

<style>
	.link-underline :global(a:hover) {
		text-decoration: underline;
	}
</style>
